import { useState } from 'react';
import { submitContactForm, ApiError, type ContactFormPayload } from '@/lib/api';

const TURNSTILE_ENABLED = !!import.meta.env.VITE_TURNSTILE_SITE_KEY;

/**
 * Shared submit logic for the Airport / Operator product forms. Reads the form
 * fields by name (uncontrolled inputs), verifies a Turnstile token is present,
 * POSTs the lead, and exposes the state the page needs to render confirmation,
 * errors, and the captcha widget.
 */
export function useContactForm(formType: ContactFormPayload['formType']) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [token, setToken] = useState('');
  const [resetSignal, setResetSignal] = useState(0);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    setError('');

    if (TURNSTILE_ENABLED && !token) {
      setError('Please complete the captcha.');
      return;
    }

    // Capture the form node before the await — React nulls currentTarget after
    // the handler returns, and we need it to reset the fields on success.
    const form = e.currentTarget;
    const fd = new FormData(form);
    const get = (k: string) => String(fd.get(k) || '').trim();
    const payload: ContactFormPayload = {
      formType,
      name: get('name'),
      company: get('company'),
      email: get('email'),
      markets: formType === 'airport' ? get('markets') : undefined,
      menuUrl: formType === 'operator' ? get('menuUrl') : undefined,
      turnstileToken: token || undefined,
    };

    setSubmitting(true);
    try {
      await submitContactForm(payload);
      // Success: clear the fields and issue a fresh captcha token (the submitted
      // one is single-use), so the form is clean if they submit again.
      form.reset();
      if (TURNSTILE_ENABLED) {
        setToken('');
        setResetSignal((n) => n + 1);
      }
      setSubmitted(true);
    } catch (err) {
      // The request consumed the Turnstile token — issue a fresh one for a retry.
      if (TURNSTILE_ENABLED) {
        setToken('');
        setResetSignal((n) => n + 1);
      }
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return {
    submitted,
    submitting,
    error,
    token,
    setToken,
    resetSignal,
    turnstileEnabled: TURNSTILE_ENABLED,
    onSubmit,
  };
}
